<template>
    <el-dialog
        :visible.sync="dialogVisible"
        width="980px"
        center
        class="voice-picker-dialog"
        @close="handleClose"
    >
        <div slot="title" class="voice-picker-dialog__title">
            <i class="iconfont icon-geren"></i>
            <span>选择发音人</span>
        </div>

        <div class="voice-picker-dialog__toolbar">
            <el-input
                v-model.trim="keyword"
                size="small"
                clearable
                placeholder="搜索发音人名称"
                class="voice-picker-dialog__search"
            >
                <i slot="prefix" class="el-icon-search"></i>
            </el-input>
            <el-select v-model="sexFilter" size="small" class="voice-picker-dialog__filter">
                <el-option label="全部性别" value=""></el-option>
                <el-option label="男" value="男"></el-option>
                <el-option label="女" value="女"></el-option>
            </el-select>
            <el-select v-model="ageFilter" size="small" class="voice-picker-dialog__filter">
                <el-option label="全部年龄段" value=""></el-option>
                <el-option label="儿童" value="儿童"></el-option>
                <el-option label="年轻人" value="年轻人"></el-option>
                <el-option label="中年人" value="中年人"></el-option>
                <el-option label="老年人" value="老年人"></el-option>
            </el-select>
        </div>

        <div class="voice-picker-dialog__summary">
            <span>共 {{ filteredVoices.length }} 个发音人</span>
            <span v-if="currentVoiceName">当前使用：{{ currentVoiceName }}</span>
            <span>点击卡片选中，点右侧按钮试听，双击卡片直接确认</span>
        </div>

        <div v-if="filteredVoices.length" class="voice-picker-dialog__grid">
            <div
                v-for="voice in filteredVoices"
                :key="voiceKey(voice)"
                class="voice-picker-card"
                tabindex="0"
                :class="{ 'voice-picker-card--active': localSelectedKey === voiceKey(voice) }"
                @click="selectVoice(voice)"
                @dblclick="confirmVoice(voice)"
                @keydown.enter.prevent="confirmVoice(voice)"
                @keydown.space.prevent="selectVoice(voice)"
            >
                <div class="voice-picker-card__header">
                    <div class="voice-picker-card__avatar">
                        <svg class="voice-picker-card__avatar-svg" viewBox="0 0 1024 1024" aria-hidden="true">
                            <use :xlink:href="avatarIcon(voice)"></use>
                        </svg>
                    </div>

                    <div class="voice-picker-card__info">
                        <div class="voice-picker-card__name">{{ voice.voicename || voice.voice || '未命名发音人' }}</div>
                        <div class="voice-picker-card__meta">
                            {{ voice.sex || '未知性别' }} / {{ voice.old || '未知年龄段' }}
                        </div>
                    </div>

                    <button
                        type="button"
                        class="voice-picker-card__preview"
                        :class="{ 'voice-picker-card__preview--playing': previewingVoiceKey === voiceKey(voice) }"
                        :disabled="!!previewLoadingKey && previewLoadingKey !== voiceKey(voice)"
                        @click.stop="emitPreview(voice)"
                    >
                        <svg class="voice-picker-card__progress" viewBox="0 0 44 44" aria-hidden="true">
                            <circle class="voice-picker-card__progress-track" cx="22" cy="22" r="18"></circle>
                            <circle
                                class="voice-picker-card__progress-bar"
                                cx="22"
                                cy="22"
                                r="18"
                                :style="{ strokeDasharray: previewCircumference, strokeDashoffset: previewDashOffset(voice) }"
                            ></circle>
                        </svg>
                        <span class="voice-picker-card__preview-icon">
                            <i v-if="previewLoadingKey === voiceKey(voice)" class="el-icon-loading"></i>
                            <template v-else-if="previewingVoiceKey === voiceKey(voice)">
                                <span class="voice-picker-card__pause-bar"></span>
                                <span class="voice-picker-card__pause-bar"></span>
                            </template>
                            <i v-else class="el-icon-headset"></i>
                        </span>
                    </button>
                </div>

                <div class="voice-picker-card__chips">
                    <span class="voice-picker-card__chip">{{ voice.language || '未知语言' }}</span>
                    <span class="voice-picker-card__chip">{{ voice.voice || voice.voicename || '默认音色' }}</span>
                </div>

                <p class="voice-picker-card__desc">{{ voiceDescription(voice) }}</p>
            </div>
        </div>

        <div v-else class="voice-picker-dialog__empty">
            <i class="el-icon-info"></i>
            <span>当前筛选条件下没有找到发音人</span>
        </div>

        <div slot="footer" class="voice-picker-dialog__footer">
            <span class="voice-picker-dialog__footer-tip">试听文案为固定示例，仅用于快速对比发音效果。</span>
            <div class="voice-picker-dialog__footer-actions">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" :disabled="!selectedVoiceObject" @click="confirmCurrentSelection">确认使用</el-button>
            </div>
        </div>
    </el-dialog>
</template>

<script>
export default {
    name: 'VoicePickerDialog',
    props: {
        visible: { type: Boolean, default: false },
        voices: { type: Array, default: () => [] },
        selectedVoiceKey: { type: String, default: '' },
        previewingVoiceKey: { type: String, default: '' },
        previewLoadingKey: { type: String, default: '' },
        previewProgress: { type: Number, default: 0 },
        currentVoiceName: { type: String, default: '' }
    },
    data() {
        return {
            keyword: '',
            sexFilter: '',
            ageFilter: '',
            localSelectedKey: ''
        }
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            }
        },
        filteredVoices() {
            const kw = String(this.keyword || '').trim().toLowerCase()
            return (this.voices || []).filter((voice) => {
                const voiceName = String((voice && (voice.voicename || voice.voice)) || '').toLowerCase()
                const sex = String((voice && voice.sex) || '')
                const age = String((voice && voice.old) || '')
                if (this.sexFilter && sex !== this.sexFilter) return false
                if (this.ageFilter && age !== this.ageFilter) return false
                if (kw && voiceName.indexOf(kw) < 0) return false
                return true
            })
        },
        selectedVoiceObject() {
            const key = String(this.localSelectedKey || '').trim()
            if (!key) return null
            return (this.voices || []).find((voice) => this.voiceKey(voice) === key) || null
        },
        previewCircumference() {
            return 2 * Math.PI * 18
        }
    },
    watch: {
        visible(value) {
            if (value) this.localSelectedKey = this.selectedVoiceKey || ''
        },
        selectedVoiceKey(value) {
            if (!this.visible) return
            this.localSelectedKey = value || ''
        }
    },
    methods: {
        voiceKey(voice) {
            if (!voice || typeof voice !== 'object') return ''
            return String(voice.id != null ? voice.id : (voice.ID != null ? voice.ID : (voice.voice || voice.voicename || '')))
        },
        avatarIcon(voice) {
            const sex = String((voice && voice.sex) || '')
            const age = String((voice && voice.old) || '')
            if (sex === '男') {
                if (age === '儿童') return '#icon-nanhai'
                if (age === '年轻人') return '#icon-nanzhiyuan'
                if (age === '中年人') return '#icon-nan'
                return '#icon-yeye'
            }
            if (age === '儿童') return '#icon-nvhai'
            if (age === '年轻人') return '#icon-nvzhiyuan'
            if (age === '中年人') return '#icon-guwen'
            return '#icon-nainai'
        },
        voiceDescription(voice) {
            const raw = String((voice && (voice.describe || voice.description || voice.desc || voice.remark)) || '').trim()
            if (raw) return raw
            const language = String((voice && voice.language) || '中文').trim()
            const sex = String((voice && voice.sex) || '未知').trim()
            const age = String((voice && voice.old) || '通用').trim()
            const voiceName = String((voice && (voice.voicename || voice.voice)) || '该发音人').trim()
            const scene = this.voiceScene(voice)
            return `${voiceName}是${language}${sex}${age}音色，适合${scene}等场景。`
        },
        voiceScene(voice) {
            const sex = String((voice && voice.sex) || '')
            const age = String((voice && voice.old) || '')
            if (age === '儿童') return '童声故事、动画配音、亲子内容'
            if (age === '老年人') return '纪实旁白、长辈角色、剧情对白'
            if (sex === '男') return age === '年轻人' ? '短视频解说、广告口播、角色对白' : '专题旁白、新闻播报、课程讲解'
            if (sex === '女') return age === '年轻人' ? '种草视频、客服播报、情感对白' : '品牌介绍、温柔旁白、节目主持'
            return '日常播报、讲解配音、通用旁白'
        },
        previewDashOffset(voice) {
            const key = this.voiceKey(voice)
            const progress = this.previewingVoiceKey === key ? Math.min(1, Math.max(0, Number(this.previewProgress || 0))) : 0
            return this.previewCircumference * (1 - progress)
        },
        selectVoice(voice) {
            this.localSelectedKey = this.voiceKey(voice)
        },
        emitPreview(voice) {
            this.localSelectedKey = this.voiceKey(voice)
            this.$emit('preview', voice)
        },
        confirmCurrentSelection() {
            if (!this.selectedVoiceObject) return
            this.confirmVoice(this.selectedVoiceObject)
        },
        confirmVoice(voice) {
            this.localSelectedKey = this.voiceKey(voice)
            this.$emit('confirm', voice)
        },
        handleClose() {
            this.$emit('close')
        }
    }
}
</script>

<style lang="less" scoped>
.voice-picker-dialog__title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}

.voice-picker-dialog__title i {
    margin-right: 8px;
    color: #409eff;
    font-size: 20px;
}

.voice-picker-dialog__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 12px;
}

.voice-picker-dialog__search {
    flex: 1 1 320px;
}

.voice-picker-dialog__search :deep(.el-input__prefix) {
    display: flex;
    align-items: center;
    height: 100%;
}

.voice-picker-dialog__search :deep(.el-input__prefix-inner) {
    display: inline-flex;
    align-items: center;
    height: 100%;
}

.voice-picker-dialog__search :deep(.el-input__icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    line-height: 1;
}

.voice-picker-dialog__filter {
    width: 140px;
}

.voice-picker-dialog__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
    margin-bottom: 16px;
    color: #909399;
    font-size: 13px;
}

.voice-picker-dialog__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
    gap: 16px;
    max-height: 60vh;
    overflow-y: auto;
    padding: 2px 2px 10px;
}

.voice-picker-card {
    border: 1px solid #e5eaf3;
    border-radius: 18px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
    padding: 18px;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.voice-picker-card:hover {
    border-color: #8ec5ff;
    box-shadow: 0 10px 24px rgba(64, 158, 255, 0.14);
    transform: translateY(-1px);
}

.voice-picker-card--active {
    border-color: #409eff;
    box-shadow: 0 12px 28px rgba(64, 158, 255, 0.18);
}

.voice-picker-card__header {
    display: flex;
    align-items: center;
    gap: 14px;
}

.voice-picker-card__avatar {
    width: 68px;
    height: 68px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid rgb(222, 226, 230);
    background: rgb(194, 194, 194);
    overflow: hidden;
}

.voice-picker-card__avatar-svg {
    width: 56px;
    height: 56px;
}

.voice-picker-card__info {
    min-width: 0;
    flex: 1;
}

.voice-picker-card__name {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    line-height: 1.4;
    word-break: break-word;
}

.voice-picker-card__meta {
    margin-top: 4px;
    font-size: 12px;
    color: #909399;
}

.voice-picker-card__preview {
    position: relative;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #409eff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
    padding: 0;
}

.voice-picker-card__preview:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.voice-picker-card__progress {
    position: absolute;
    inset: 0;
    width: 52px;
    height: 52px;
}

.voice-picker-card__progress-track,
.voice-picker-card__progress-bar {
    fill: none;
    stroke-width: 3.5;
}

.voice-picker-card__progress-track {
    stroke: rgba(64, 158, 255, 0.18);
}

.voice-picker-card__progress-bar {
    stroke: #409eff;
    stroke-linecap: round;
    transform: rotate(-90deg);
    transform-origin: 22px 22px;
    transition: stroke-dashoffset 0.12s linear;
}

.voice-picker-card__preview-icon {
    position: relative;
    z-index: 1;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #ffffff;
    border: 1px solid #d8e9ff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    box-shadow: 0 4px 12px rgba(64, 158, 255, 0.12);
    transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.voice-picker-card__preview:hover:not(:disabled) .voice-picker-card__preview-icon,
.voice-picker-card__preview--playing .voice-picker-card__preview-icon {
    background: #409eff;
    color: #fff;
    border-color: #409eff;
}

.voice-picker-card__pause-bar {
    width: 3px;
    height: 12px;
    border-radius: 999px;
    background: currentColor;
}

.voice-picker-card__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
}

.voice-picker-card__chip {
    max-width: 100%;
    padding: 5px 10px;
    border-radius: 999px;
    background: #f4f8ff;
    border: 1px solid #e4eefc;
    color: #606266;
    font-size: 12px;
    line-height: 1.4;
    word-break: break-word;
}

.voice-picker-card__desc {
    margin: 14px 0 0;
    min-height: 44px;
    color: #5f6b7a;
    font-size: 13px;
    line-height: 1.7;
}

.voice-picker-dialog__empty {
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #909399;
    font-size: 14px;
}

.voice-picker-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.voice-picker-dialog__footer-tip {
    color: #909399;
    font-size: 12px;
}

.voice-picker-dialog__footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

@media (max-width: 768px) {
    .voice-picker-dialog :deep(.el-dialog) {
        width: 94vw !important;
        margin-top: 4vh !important;
    }

    .voice-picker-dialog__toolbar {
        gap: 10px;
    }

    .voice-picker-dialog__search,
    .voice-picker-dialog__filter {
        width: 100%;
        flex: 1 1 100%;
    }

    .voice-picker-dialog__grid {
        grid-template-columns: 1fr;
        max-height: 58vh;
    }

    .voice-picker-card {
        padding: 16px 14px;
    }

    .voice-picker-card__header {
        gap: 10px;
    }

    .voice-picker-card__avatar {
        width: 60px;
        height: 60px;
    }

    .voice-picker-card__avatar-svg {
        width: 48px;
        height: 48px;
    }

    .voice-picker-card__preview {
        width: 48px;
        height: 48px;
    }

    .voice-picker-card__progress {
        width: 48px;
        height: 48px;
    }

    .voice-picker-card__desc {
        min-height: auto;
    }

    .voice-picker-dialog__footer {
        flex-direction: column;
        align-items: stretch;
    }

    .voice-picker-dialog__footer-actions {
        justify-content: flex-end;
    }

    .voice-picker-dialog__footer-tip {
        text-align: center;
    }
}
</style>

<style lang="less">
.dark-mode .voice-picker-dialog .el-dialog {
    background: var(--bg-card) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-dialog__title,
.dark-mode .voice-picker-dialog .voice-picker-card__name {
    color: var(--text-primary) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-dialog__summary,
.dark-mode .voice-picker-dialog .voice-picker-card__meta,
.dark-mode .voice-picker-dialog .voice-picker-dialog__footer-tip {
    color: var(--text-secondary) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card {
    background: linear-gradient(180deg, rgba(30, 41, 59, 0.96) 0%, rgba(15, 23, 42, 0.98) 100%) !important;
    border-color: var(--border-color) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__desc {
    color: var(--text-regular) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__avatar {
    border-color: var(--border-color) !important;
    background: rgba(148, 163, 184, 0.28) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__preview-icon {
    background: rgba(15, 23, 42, 0.88) !important;
    border-color: rgba(64, 158, 255, 0.28) !important;
    color: var(--text-primary) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__chip {
    background: rgba(148, 163, 184, 0.1) !important;
    border-color: rgba(148, 163, 184, 0.18) !important;
    color: var(--text-regular) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__progress-track {
    stroke: rgba(125, 195, 255, 0.16) !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__progress-bar {
    stroke: #7dc3ff !important;
}

.dark-mode .voice-picker-dialog .voice-picker-card__preview:hover:not(:disabled) .voice-picker-card__preview-icon,
.dark-mode .voice-picker-dialog .voice-picker-card__preview--playing .voice-picker-card__preview-icon {
    background: #409eff !important;
    color: #fff !important;
    border-color: #409eff !important;
}
</style>
